// src/UserManagement.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db, firebaseConfig } from './firebaseConfig'; // Importamos o firebaseConfig
import { collection, onSnapshot, doc, setDoc, deleteDoc } from 'firebase/firestore';

// Importações especiais para a Segunda Instância
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut } from 'firebase/auth';

function UserManagement({ userRole }) {
  const [dbUsers, setDbUsers] = useState([]); 
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('viewer');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const unsubDbUsers = onSnapshot(collection(db, "users"), (snapshot) => {
      setDbUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubDbUsers();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsLoading(true);

    if (userRole !== 'admin') {
      setError("Você não tem permissão para criar usuários.");
      setIsLoading(false);
      return;
    }

    let secondaryApp;
    try {
      // 1. INICIA UMA SEGUNDA INSTÂNCIA DO FIREBASE (Temporária)
      // Isso isola a criação do usuário para não afetar sua sessão principal logada
      secondaryApp = initializeApp(firebaseConfig, "SecondaryApp");
      const secondaryAuth = getAuth(secondaryApp);

      // 2. Cria o usuário nessa instância temporária
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, newEmail, newPassword);
      const newUid = userCredential.user.uid;

      // 3. Desloga da instância temporária e a deleta para limpar a memória
      await signOut(secondaryAuth);
      await deleteApp(secondaryApp);

      // 4. Salva o papel (role) no Firestore usando a sua sessão PRINCIPAL (que ainda é Admin)
      await setDoc(doc(db, "users", newUid), {
        email: newEmail,
        role: newRole
      });

      setMessage(`Usuário ${newEmail} criado com sucesso e definido como ${newRole}.`);
      setNewEmail('');
      setNewPassword('');
      setNewRole('viewer');

    } catch (err) {
      console.error("Erro ao criar usuário:", err.message);
      if (secondaryApp) {
        await deleteApp(secondaryApp).catch(console.error); // Garante a limpeza em caso de erro
      }
      
      if (err.code === 'auth/email-already-in-use') {
        setError("O e-mail já está em uso.");
      } else if (err.code === 'auth/weak-password') {
        setError("A senha deve ter pelo menos 6 caracteres.");
      } else {
        setError("Erro ao criar usuário. Verifique os dados.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeRole = async (userId, currentEmail, role) => {
    if (userRole !== 'admin') return;
    const newRoleValue = window.prompt(`Definir novo papel para ${currentEmail} (admin ou viewer):`, role);
    if (newRoleValue && (newRoleValue === 'admin' || newRoleValue === 'viewer')) {
      try {
        await setDoc(doc(db, "users", userId), { email: currentEmail, role: newRoleValue });
        setMessage(`Papel de ${currentEmail} alterado para ${newRoleValue}.`);
      } catch (err) {
        setError("Erro ao alterar papel.");
      }
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (userRole !== 'admin') return;
    if (window.confirm(`Tem certeza que deseja remover o papel de ${userEmail}?`)) {
      try {
        await deleteDoc(doc(db, "users", userId));
        setMessage(`Papel de ${userEmail} removido.`);
      } catch (err) {
        setError("Erro ao deletar papel do Firestore.");
      }
    }
  };

  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-xl text-red-700">Acesso negado. Apenas administradores podem gerenciar usuários.</p>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-100 min-h-screen">
      <Link to="/" className="text-blue-600 font-bold mb-6 block">&larr; Voltar ao Dashboard</Link>
      <h2 className="text-2xl font-bold mb-6">Gerenciamento de Usuários</h2>

      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h3 className="text-xl font-bold mb-4">Criar Novo Usuário</h3>
        <form onSubmit={handleCreateUser} className="space-y-4">
          <input type="email" placeholder="E-mail" className="w-full p-2 border rounded" value={newEmail} onChange={e => setNewEmail(e.target.value)} required />
          <input type="password" placeholder="Senha (mín. 6 caracteres)" className="w-full p-2 border rounded" value={newPassword} onChange={e => setNewPassword(e.target.value)} required />
          <select className="w-full p-2 border rounded" value={newRole} onChange={e => setNewRole(e.target.value)}>
            <option value="viewer">Viewer</option>
            <option value="admin">Admin</option>
          </select>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          {message && <p className="text-green-500 text-sm">{message}</p>}
          <button type="submit" disabled={isLoading} className={`w-full p-2 rounded text-white font-bold ${isLoading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {isLoading ? 'Criando...' : 'Criar Usuário'}
          </button>
        </form>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-xl font-bold mb-4">Usuários do Sistema</h3>
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50 text-gray-600 text-sm">
              <th className="p-3 border-b">E-mail</th>
              <th className="p-3 border-b">Papel</th>
              <th className="p-3 border-b">Ações</th>
            </tr>
          </thead>
          <tbody>
            {dbUsers.map(u => (
              <tr key={u.id} className="border-b hover:bg-gray-50 text-sm">
                <td className="p-3 font-medium">{u.email}</td>
                <td className="p-3 capitalize">{u.role}</td>
                <td className="p-3 flex gap-2">
                  <button onClick={() => handleChangeRole(u.id, u.email, u.role)} className="text-blue-600 hover:underline">Alterar Papel</button>
                  <button onClick={() => handleDeleteUser(u.id, u.email)} className="text-red-600 hover:underline">Remover Papel</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
export default UserManagement;